import PropTypes from "prop-types";
import React, { Component, Fragment } from "react";
import { SelectionControl, TimePicker } from "react-md";
import Card from "react-md/lib/Cards/Card";
import CardText from "react-md/lib/Cards/CardText";
import { DateField, TextField } from "components/Shared/MGridForm/sub-components";
import { cloneDeep } from "lodash";
import { getHashCode } from "components/Shared/Functions";
import { MSelect, MSpinner, TippedIcon } from "components/Shared";
import { dateToString } from "components/Shared/Formatters";
import { parseSections, useYup } from "components/Shared/MGridForm/utility";
import {
  normalizePhoneExt,
  normalizeIntegerMoney,
  normalizeZip,
  normalizeIntegerFormatted,
  normalizeYear,
} from "components/FormElements";
import { scrollToRequired_Focus, scrollToRequired_ScrollTo } from "components/FormElements/ScrollToRequired";
import {
  DATA_TYPE_NONE,
  DATA_TYPE_STRING,
  DATA_TYPE_MULTI_LINE_STRING,
  DATA_TYPE_CHECKBOX,
  DATA_TYPE_RADIO,
  DATA_TYPE_SPLIT_RADIO,
  DATA_TYPE_DROPDOWN,
  DATA_TYPE_DROPDOWN2,
  DATA_TYPE_DATE,
  DATA_TYPE_PHOTO,
  DATA_TYPE_PHONE_NUMBER,
  DATA_TYPE_MONEY,
  DATA_TYPE_ZIP,
  DATA_TYPE_NUMBER,
  DATA_TYPE_YEAR,
  DATA_TYPE_TOOLTIP,
  DATA_TYPE_TIME,
} from "typeConstants/dataTypes";

class MGridForm extends Component {
  constructor(props) {
    super(props);

    this.renderRow = this.renderRow.bind(this);
    this.renderElement = this.renderElement.bind(this);
    this.getBorder = this.getBorder.bind(this);
    this.getValueFromProps = this.getValueFromProps.bind(this);
    this.onFieldChange = this.onFieldChange.bind(this);
    this.initializeFormData = this.initializeFormData.bind(this);
    this.getRef = this.getRef.bind(this);
    this.initializeAfterDataLoad = this.initializeAfterDataLoad.bind(this);
    this.trackChangedValueInModel = this.trackChangedValueInModel.bind(this);
    this.getEnabledFields = this.getEnabledFields.bind(this);
    this.getAllValidationErrorFields = this.getAllValidationErrorFields.bind(this);
    this.getValidationError = this.getValidationError.bind(this);
    this.checkValidationErrors = this.checkValidationErrors.bind(this);
    this.isFormValid = this.isFormValid.bind(this);
    this.getFieldValue = this.getFieldValue.bind(this);
    this.getModifiedData = this.getModifiedData.bind(this);
    this.setFieldModified = this.setFieldModified.bind(this);
    this.getScrollToRequiredFields = this.getScrollToRequiredFields.bind(this);

    this.inputRefs = {};

    this.changedFields = null;
    this.changedModel = null;
    this.enabledFields = null;
    this.validationErrorFields = {};
  }

  componentDidMount() {
    if (this.props.formDefinition) this.initializeFormData();
    if (this.props.data) this.initializeAfterDataLoad();

    if (this.props.getControlMethods) {
      this.props.getControlMethods({
        isFormValid: this.isFormValid,
        getModifiedData: this.getModifiedData,
        setFieldModified: this.setFieldModified,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.formDefinition && this.props.formDefinition) this.initializeFormData();
    if (!prevProps.data && this.props.data) this.initializeAfterDataLoad();
    if (this.props.data) this.enabledFields = this.getEnabledFields();
    if (prevProps.elementIdInFocus !== this.props.elementIdInFocus
      && this.props.onScrollToRequiredFieldsChange) {
      this.props.onScrollToRequiredFieldsChange(this.getScrollToRequiredFields());
    }
  }

  renderRow(r, i) {
    let rowkey = r.reduce(
      (acc, current) => acc + (current.source || current.label || ""),
      ""
    );
    if (!rowkey)
      rowkey = `${i}`;

    return (
      <div key={`row-${rowkey}`} className="mgridform-row">
        {r.map((e, j) => this.renderElement(e, j, i === this.props.formDefinition.length - 1))}
      </div>
    );
  }

  initializeFormData() {
    this.changedFields = new Set();

    this.props.formDefinition.forEach(r => r.forEach(e => {
      if (e.source) {
        switch (e.dataType) {
          case DATA_TYPE_RADIO:
            if (e.dataTypeOptions && e.dataTypeOptions.options) e.dataTypeOptions.options.forEach((o, i) => {
              this.inputRefs[`${e.source}_${i}`] = React.createRef();
            });
            break;
          case DATA_TYPE_STRING:
          case DATA_TYPE_MULTI_LINE_STRING:
          case DATA_TYPE_CHECKBOX:
          case DATA_TYPE_DATE:
            this.inputRefs[e.source] = React.createRef();
            break;
          case DATA_TYPE_SPLIT_RADIO:
            this.inputRefs[e.source + e.value] = React.createRef();
            break;
        }
      }
    }));
  }

  initializeAfterDataLoad() {
    this.changedModel = cloneDeep(this.props.data);

    //on first load once the changedModel is loaded here we want to render
    //i dont put the changedModel in the state because i dont want the component updating every time we change a field
    //re rendering the entire form every time this changes takes a lot of time
    this.forceUpdate();
  }

  getEnabledFields() {
    return this.props.formDefinition.flat().filter(fd => fd.source && (!fd.enabled || fd.enabled(this.changedModel, fd.source, this.changedModel[fd.source]))).map(f => f.source);
  }

  getModifiedData() {
    return [...this.changedFields].filter(f => this.enabledFields.includes(f)).map(f => ({ id: f, value: this.changedModel[f] }));
  }

  setFieldModified(id, val) {
    this.trackChangedValueInModel(id, val);
  }

  isFormValid() {
    let oldValidationErrors = cloneDeep(this.validationErrorFields);
    this.validationErrorFields = this.getAllValidationErrorFields();

    let oldKeys = Object.keys(oldValidationErrors);
    let newKeys = Object.keys(this.validationErrorFields);

    if (oldKeys.length != newKeys.length ||
      !oldKeys.reduce((acc, f) => acc && newKeys.includes(f) && oldValidationErrors[f] == this.validationErrorFields[f], true)) {
      if (this.props.onScrollToRequiredFieldsChange) {
        this.props.onScrollToRequiredFieldsChange(this.getScrollToRequiredFields());
      }
      this.forceUpdate();
    }

    return newKeys.length == 0;
  }

  getAllValidationErrorFields() {
    return this.props.formDefinition.flat().reduce((acc, fd) => {
      let error = this.getValidationError(fd);
      if (error) acc[fd.source] = error;
      return acc;
    }, {});
  }

  getValidationError(field) {
    return (field.hasValidationError && this.enabledFields.indexOf(field.source) >= 0) ? field.hasValidationError(this.changedModel, field.source, this.getFieldValue(this.changedModel, field.source)) : false;
  }

  getFieldValue(changeModel, fieldSource) {

    let path = fieldSource.split(".");

    if (path.length == 0 || !changeModel[path[0]] || !changeModel[path[0]][path[1]]) return changeModel[path[0]];

    return changeModel[path[0]][path[1]];
  }

  getRef(key, i, value) {
    if (i != null) key += "_" + i;
    if (value != null) key += value;
    return this.inputRefs[key];
  }

  getBorder(border) {
    if (typeof (border) === "number") return `${border}px solid var(--mgridform-border-color)`;
    else return border;
  }

  getValueFromProps(source) {
    return this.props.data && source && source.split(".").reduce((acc, val) => acc[val], this.props.data);
  }

  setValue(obj, field, value) {
    let arr = field.split(".");
    let lastField = arr.pop();

    obj = arr.reduce((acc, val) => acc[val], obj);
    obj[lastField] = value;
  }

  onFieldChange(e, value) {
    // TODO: this should probably be a switch

    if (e.dataType === DATA_TYPE_RADIO && e.dataTypeOptions && e.dataTypeOptions.options) {
      e.dataTypeOptions.options.forEach((o, i) => {
        let ref = this.getRef(e.source, i);
        if (o.value == value) {
          if (ref.current.state.checked === true) {
            value = null;
            ref.current.setState({ checked: false });
          }
        } else {
          ref.current.setState({ checked: false });
        }
      });
    }
    //get adjustments from the form definition
    if (e.validationAdjustments) {
      let adjustments = e.validationAdjustments(e, value);
      adjustments  && adjustments.forEach((data) => {
        let adjRef = this.getRef(data[0]);
        if (adjRef != null) adjRef.current.setState(data[1]);
        this.trackChangedValueInModel(data[0], false);
      });
    }

    //lets handle the "noneOption" checkboxes
    //if a checkbox is checked and has a none option, we need to make sure we uncheck the none option
    //alternativeley, if a none option box is checked we need to uncheck all of the boxes that reference it
    if (e.dataType === DATA_TYPE_CHECKBOX) {
      value = !this.getRef(e.source).current.state.checked;

      if (e.dataTypeOptions && e.dataTypeOptions.checkedValue !== undefined && e.dataTypeOptions.uncheckedValue !== undefined) {
        value = value ? e.dataTypeOptions.checkedValue : e.dataTypeOptions.uncheckedValue;
      } else {
        if (value) {
          if (e.dataTypeOptions && e.dataTypeOptions.noneOption) {
            this.getRef(e.dataTypeOptions.noneOption).current.setState({ checked: false });
            this.trackChangedValueInModel(e.dataTypeOptions.noneOption, false);
          }

          this.props.formDefinition.flat().forEach(fd => {
            if (fd.dataTypeOptions && fd.dataTypeOptions.noneOption == e.source) {
              this.getRef(fd.source).current.setState({ checked: false });
              this.trackChangedValueInModel(fd.source, false);
            }
          });
        }
      }
    }

    if (e.dataType === DATA_TYPE_SPLIT_RADIO) {
      //not negating this here because the state has not yet been updated, so it will actually still be checked
      if (this.getRef(e.source, null, e.value).current.state.checked) value = null;

      if (value) {
        this.props.formDefinition.flat().forEach(fd => {
          if (fd.source === e.source && fd.value !== e.value) {
            this.getRef(fd.source, null, fd.value).current.setState({ checked: false });
          }
        });
      }
    }

    if (e.dataType === DATA_TYPE_DROPDOWN || e.dataType === DATA_TYPE_DROPDOWN2) {
      if (e.dataTypeOptions && !e.dataTypeOptions.isMulti) {
        value = value.value;
      }
    }

    if (e.dataType === DATA_TYPE_DATE) {
      if (value) value = dateToString(value);
      // set date picker value to null if the user cleared the value
      else this.getRef(e.source).current.setState({ value: null });
    }

    this.trackChangedValueInModel(e.source, value);

    let oldEnabledFields = this.enabledFields;
    this.enabledFields = this.getEnabledFields();

    if (oldEnabledFields.length != this.enabledFields.length ||
      !oldEnabledFields.reduce((acc, f) => acc && this.enabledFields.includes(f), true)) {

      //remove all validation errors on disabled fields
      Object.keys(this.validationErrorFields).forEach(k => {
        if (this.enabledFields.indexOf(k) == -1) delete this.validationErrorFields[k];
      });

      this.forceUpdate();
    }

    if (this.props.onChange) {
      this.props.onChange(this.changedModel, e.source, e.dataType, value, [...this.changedFields]);
    }
  }

  checkValidationErrors(fd) {
    let validationGroup = this.props.validationGroups ?.find(g => g.includes(fd.source));
    let formDefinition = validationGroup ? this.props.formDefinition.flat().filter(f => validationGroup.includes(f.source)) : [fd];
    for (let e of formDefinition) {
      let validationError = this.getValidationError(e);

      //this.validationErrorFields[e.source] can be either undefined (because we dont have an error logged for it), true, false, null, or a string
      //validationError is expected to be true, false, null, or a string
      //here i need to check if the error changed, but its not as easy as just saying this.validationErrorFields[e.source] != validationError because the lhs might be undefined while the rhs could be false, and then we would still enter the if statement
      //so i had to write some more complex stuff

      if ((this.validationErrorFields[e.source] == undefined && validationError) ||
        (this.validationErrorFields[e.source] != undefined && this.validationErrorFields[e.source] != validationError)) {
        if (validationError) this.validationErrorFields[e.source] = validationError;
        else delete this.validationErrorFields[e.source];
      }
    }
    if (this.props.onScrollToRequiredFieldsChange) this.props.onScrollToRequiredFieldsChange(this.getScrollToRequiredFields());
    if(this.props.onBlur) this.props.onBlur(fd, this.changedModel[fd.source]);
    this.forceUpdate();
  }

  trackChangedValueInModel(source, value) {
    this.changedFields.add(source);
    this.setValue(this.changedModel, source, value);
  }

  renderElement(e, i, lastRow) {
    const { setElementIdInFocus } = this.props;
    let styles = {};

    if (!lastRow) styles.borderBottom = "1px solid var(--mgridform-border-color)";

    ["Top", "Bottom", "Left", "Right"].forEach(b => {
      b = `border${b}`;
      if (e.style && e.style[b] !== null && e.style[b] !== undefined) styles[b] = this.getBorder(e.style[b]);
    });

    if (e.width) styles.width = `${e.width}%`;
    else styles.flex = 1;

    let inner;
    let labelStyle = e.labelWidth ? { width: `${e.labelWidth}%` } : {};
    let label = e.label && <div style={labelStyle} className="mgridform-label">{e.label}</div>;
    let dataTypeClass;
    let value = this.getValueFromProps(e.source);
    let cbVal;
    let active = e.source === this.props.elementIdInFocus ? "mgridform-active" : "";

    // To make width alingment easier, keeping div without children
    let visible = e.visible ? e.visible(this.changedModel, e.source, value) : true;
    if (!visible) return;

    let enabled = e.enabled ? e.enabled(this.changedModel, e.source, value) : true;
    // Not currently enabled and readOnly is not specified or is true
    let alwaysDisabled = !enabled && (e.readOnly === undefined || e.readOnly === true);
    let validationMessage = this.validationErrorFields[e.source];
    let validationClass = validationMessage ? "invalid-element" : "";

    if (typeof validationMessage == "boolean" && validationMessage) validationMessage = "This field is required";

    let disabledClass = enabled ? "" : "disabled-element";

    let getCheckboxValue = (f, v) => {
      if (f.dataTypeOptions && f.dataTypeOptions.checkedValue !== undefined && f.dataTypeOptions.uncheckedValue !== undefined) {
        return v == f.dataTypeOptions.checkedValue;
      } else {
        let isChecked = v;
        if (typeof (v) === "string") isChecked = v.toLowerCase().startsWith("t") || v === "1";
        return isChecked;
      }
    };

    let dynamicStyle = e.styleCallback ? e.styleCallback(this.changedModel, e.source, value) : "";

    let onFieldChange = v => this.onFieldChange(e, v);
    let checkValidationErrors = () => this.checkValidationErrors(e);

    switch (e.dataType) {
      case DATA_TYPE_STRING:
        dataTypeClass = "mgridform-text-input";
        inner = (
          <TextField
            label={label}
            innerRef={this.getRef(e.source)}
            id={e.source || ""}
            defaultValue={value || ""}
            onChange={onFieldChange}
            onBlur={checkValidationErrors}
            disabled={!enabled}
            readOnly={e.readOnly}
            onFocus={() => setElementIdInFocus(e.source)}
          />
        );
        if (alwaysDisabled){
          inner = React.cloneElement(inner, { value: value || ""});
        }
        break;
      case DATA_TYPE_MULTI_LINE_STRING:
        dataTypeClass = "mgridform-multi-line-input";
        inner = (
          <TextField
            label={label}
            innerRef={this.getRef(e.source)}
            id={e.source || ""}
            defaultValue={value || ""}
            rows={e.dataTypeOptions ? e.dataTypeOptions.defaultLines : 1}
            onChange={onFieldChange}
            onBlur={checkValidationErrors}
            disabled={!enabled}
            readOnly={e.readOnly}
            onFocus={() => setElementIdInFocus(e.source)}
          />
        );
        if (alwaysDisabled){
          inner = React.cloneElement(inner, { value: value || ""});
        }
        break;
      case DATA_TYPE_SPLIT_RADIO:
      case DATA_TYPE_CHECKBOX:
        {
          let ref;
          let newValue;
          let id;
          if (e.dataType === DATA_TYPE_SPLIT_RADIO) {
            dataTypeClass = "mgridform-split-radio";
            ref = this.getRef(e.source, null, e.value);
            newValue = e.value;
            id = e.source + e.value;
            cbVal = this.changedModel[e.source] === e.value;
          } else {
            dataTypeClass = "mgridform-checkbox";
            ref = this.getRef(e.source);
            newValue = null; //we will figure out the value with a ref in the on field change method
            id = e.source;
            cbVal = getCheckboxValue(e, value);
          }

          inner = (
              <SelectionControl
                  type="checkbox"
                  ref={ref}
                  id={id || ""}
                  aria-label={e.label || ""}
                  name={e.label || ""}
                  defaultChecked={cbVal || false}
                  onChange={() => { onFieldChange(newValue); checkValidationErrors(); }}
                  disabled={!enabled}
                  onFocus={() => setElementIdInFocus(id)}
              />
          );
          if (alwaysDisabled){
            inner = React.cloneElement(inner, { checked: cbVal || ""});
          }

          inner = (
            <div className="mgridform-value">
              {inner}
              {e.label && <div className="mgridform-checkbox-label">{e.label}</div>}
            </div>
          );
        }
        break;
      case DATA_TYPE_RADIO:
        dataTypeClass = "mgridform-checkbox";
        inner = (
          <Fragment>
            {label}
            {e.dataTypeOptions.options.map((o, j) => (
              <div key={`cd-value-${j}`} className="mgridform-value" style={o.width && { width: `${o.width}%` }} >
                <SelectionControl
                  type="checkbox"
                  ref={this.getRef(e.source, j)}
                  id={`${e.source}-${getHashCode(o.value)}`}
                  aria-label={e.label || ""}
                  name={e.label || ""}
                  defaultChecked={value === o.value || false}
                  onChange={() => { onFieldChange(o.value); checkValidationErrors(); }}
                  disabled={!enabled}
                  onFocus={() => setElementIdInFocus(e.source)}
                  label={o.label}
                  labelTextClassName="mgridform-checkbox-label"
                />
              </div>
            ))}
          </Fragment>
        );
        break;
      case DATA_TYPE_DROPDOWN:
      case DATA_TYPE_DROPDOWN2:
        dataTypeClass = "mgridform-dropdown";
        inner = (
          <Fragment>
            {label}
            <MSelect
              id={e.source}
              className="mgridform-mselect"
              //sortFunc={field.dataTypeOptions.sortFunc}
              defaultValue={e.dataTypeOptions && e.dataTypeOptions.options ? e.dataTypeOptions.options.find(o => o.value === value) : null}
              onChange={v => { onFieldChange(v); checkValidationErrors(); }}
              options={e.dataTypeOptions ? e.dataTypeOptions.options : []}
              isClearable={false}
              showArrow={false}
              isDisabled={!enabled}
              enablePortal={this.props.portalTarget ? true : false}
              getMenuPortalTarget={this.props.portalTarget}
              isMulti={e.dataTypeOptions && e.dataTypeOptions.isMulti ? e.dataTypeOptions.isMulti : false}
              shortMulti={false}
              onFocus={() => setElementIdInFocus(e.source)}
              placeholder={e.dataTypeOptions?.placeholder}
            />
          </Fragment>
        );
        break;
      case DATA_TYPE_DATE:
        dataTypeClass = "mgridform-date";
        if (!Date.parse(value)) value = null;
        inner = (
          <DateField
            {...{ label }}
            innerRef={this.getRef(e.source)}
            id={e.source}
            defaultValue={value}
            disabled={!enabled}
            minDate={e.dataTypeOptions ? e.dataTypeOptions.minDate : undefined}
            maxDate={e.dataTypeOptions ? e.dataTypeOptions.maxDate : undefined}
            helpText={e.dataTypeOptions ? e.dataTypeOptions.helpText : undefined}
            onChange={(s, d) => {
              onFieldChange(d);
              // onBlur does not fire for this component when you would expect it so validation is done on change
              checkValidationErrors();
              // there is no onFocus for this component
              setElementIdInFocus(e.source);
            }}
            onClear={() => onFieldChange(null)}
          />
        );
        if (alwaysDisabled){
          inner = React.cloneElement(inner, { value: value });
        }
        break;
      case DATA_TYPE_TIME:
        inner = (
          <>
            <span>{ label }</span>
            <TimePicker
              id={e.source}
              autoOk
              onChange={(s, d) => onFieldChange(d)}
              value={value}
              portal
              lastChild
              renderNode={null}
              disableScrollLocking
            />
          </>
        );
        break;
      case DATA_TYPE_PHOTO:
        dataTypeClass = "mgridform-text-photo";
        inner = (
          <Fragment>
            {label}
            <div className="mgridform-photo-wrapper">
              <Card
                className="mcard card-unnested"
                onClick={() => value.hasOwnProperty("src") ? e.displayPhoto(value.idx) : {}}>
                <CardText> {value.hasOwnProperty("src") &&
                  <img
                    className="mgridform-photo"
                    ref={this.getRef(e.source)}
                    id={e.source || ""}
                    src={value.src || ""} />}
                </CardText>
              </Card>
            </div>
          </Fragment>);
        break;
      case DATA_TYPE_PHONE_NUMBER:
        dataTypeClass = "mgridform-text-input";
        inner = (
          <TextField
            {...{ label }}
            innerRef={this.getRef(e.source)}
            id={e.source || ""}
            defaultValue={normalizePhoneExt(value) || ""}
            onChange={onFieldChange}
            onBlur={checkValidationErrors}
            disabled={!enabled}
            onFocus={() => setElementIdInFocus(e.source)}
          />
        );
        if (alwaysDisabled){
          inner = React.cloneElement(inner, { value: normalizePhoneExt(value) || "" });
        }
        break;
      case DATA_TYPE_MONEY:
        dataTypeClass = "mgridform-text-input";
        inner = (
          <TextField
            {...{ label }}
            innerRef={this.getRef(e.source)}
            id={e.source || ""}
            defaultValue={normalizeIntegerMoney(value) || ""}
            onChange={onFieldChange}
            onBlur={checkValidationErrors}
            disabled={!enabled}
            onFocus={() => setElementIdInFocus(e.source)}
          />
        );
        if (alwaysDisabled){
          inner = React.cloneElement(inner, { value: normalizeIntegerMoney(value) || "" });
        }
        break;
      case DATA_TYPE_ZIP:
        dataTypeClass = "mgridform-text-input";
        inner = (
          <TextField
            {...{ label }}
            innerRef={this.getRef(e.source)}
            id={e.source || ""}
            defaultValue={normalizeZip(value) || ""}
            onChange={onFieldChange}
            onBlur={checkValidationErrors}
            disabled={!enabled}
            onFocus={() => setElementIdInFocus(e.source)}
          />
        );
        if (alwaysDisabled){
          inner = React.cloneElement(inner, { value: normalizeZip(value) || "" });
        }
        break;
      case DATA_TYPE_NUMBER:
        dataTypeClass = "mgridform-text-input";
        inner = (
          <TextField
            {...{ label }}
            innerRef={this.getRef(e.source)}
            id={e.source || ""}
            defaultValue={normalizeIntegerFormatted(value) || ""}
            onChange={onFieldChange}
            onBlur={checkValidationErrors}
            disabled={!enabled}
            onFocus={() => setElementIdInFocus(e.source)}
          />
        );
        if (alwaysDisabled){
          inner = React.cloneElement(inner, { value: normalizeIntegerFormatted(value) || "" });
        }
        break;
      case DATA_TYPE_YEAR:
        dataTypeClass = "mgridform-text-input";
        inner = (
          <TextField
            {...{ label }}
            innerRef={this.getRef(e.source)}
            id={e.source || ""}
            defaultValue={normalizeYear(value) || ""}
            onChange={onFieldChange}
            onBlur={checkValidationErrors}
            disabled={!enabled}
            onFocus={() => setElementIdInFocus(e.source)}
          />
        );
        if (alwaysDisabled){
          inner = React.cloneElement(inner, { value: normalizeYear(value) || "" });
        }
        break;
      case DATA_TYPE_TOOLTIP:
        if (enabled) {
          inner = (
            <TippedIcon
              iconName="info"
              tooltipText={label}
              iconClassName={"grid-question-tooltip"}
              tooltipPopoutDirection={e.direction}
            />
          );
        } else {
          disabledClass = "";
          inner = <div />;
        }
        break;
      case DATA_TYPE_NONE:
      default:
        dataTypeClass = "mgridform-none";
        inner = label;
        break;
    }

    let displayStyleClass = "";

    if (e.style && e.style.className) {
      if (Array.isArray(e.style.className)) {
        displayStyleClass = e.style.className.flat().join(" ");
      } else {
        displayStyleClass = e.style.className;
      }
    }

    if(e.highlightField) {
      styles.backgroundColor = "lightyellow";
    }

    if(e.onTooltipClicked) {
      inner = (
        <>
          <TippedIcon
              iconName="mode_edit_outline"
              tooltipText={e.toolTipText}
              iconClassName={"grid-question-very-small-tooltip"}
              onTooltipClicked={() => e.onTooltipClicked(e)}
              tooltipPopoutDirection="right"
          />
          {inner}
        </>);
    }

    return (
      <div key={`cell-${i}`} {...validationMessage && { title: validationMessage }} style={styles} className={`mgridform-row-element ${dataTypeClass} ${displayStyleClass} ${disabledClass} ${validationClass} ${dynamicStyle}  ${active}`}>
        {inner}
      </div>
    );
  }

  getScrollToRequiredFields() {
    // Returns only ONE inputRef for DATA_TYPE_RADIO
    // Reason: If a field has 4 options, there will be 4 inputRefs for it
    // ScrollToRequired will get to the same location 4 times which is not desired
    const { elementIdInFocus } = this.props;
    const findDomElorRef = (e) => {
      if (e.source) {
        switch (e.dataType) {
          case DATA_TYPE_RADIO:
            return { id: e.source, element: document.getElementById(`${e.source}-${getHashCode(e.dataTypeOptions.options[0].value)}`), scrollFunc: scrollToRequired_ScrollTo, inFocus: elementIdInFocus === e.source };
          case DATA_TYPE_STRING:
          case DATA_TYPE_MULTI_LINE_STRING:
            return { id: e.source, element: this.inputRefs[e.source].current, scrollFunc: scrollToRequired_Focus, inFocus: elementIdInFocus === e.source };
          case DATA_TYPE_CHECKBOX:
          case DATA_TYPE_DROPDOWN:
            return { id: e.source, element: document.getElementById(e.source), scrollFunc: scrollToRequired_ScrollTo, inFocus: elementIdInFocus === e.source };
          case DATA_TYPE_DATE:
            // Special case coz returning ref would use scrollTo in <ScrollToRequired>
            // Datepicker returns wrong offsetTop (reason unknown) but can be focused
            return { id: e.source, element: document.getElementById(e.source), scrollFunc: scrollToRequired_Focus, inFocus: elementIdInFocus === e.source };
          case DATA_TYPE_SPLIT_RADIO:
            return { id: e.source + e.value, element: document.getElementById(e.source + e.value), scrollFunc: scrollToRequired_ScrollTo, inFocus: elementIdInFocus === e.source + e.value };
          default:
            return { id: e.source, element: document.getElementById(e.source), scrollFunc: scrollToRequired_Focus, inFocus: elementIdInFocus === e.source };
        }
      }
    };

    if (!this.props.formDefinition) return [];

    let fields = this.props.formDefinition
      .flat()
      .map(f => ({ ...findDomElorRef(f), valid: !this.validationErrorFields[f.source] }));
    return fields;
  }

  render() {
    if (!this.props.data || !this.changedModel) return <MSpinner />;
    const { webform, width } = this.props;
    return (
      <div className={`mgridform-container${webform ? " mgridform-webform" : ""}`} style={{ width: (width ? `${width}px` : "100%") }}>
        {this.props.formDefinition.map(this.renderRow)}
      </div>
    );
  }
}

MGridForm.defaultProps = {
  webform: false,
  setElementIdInFocus: () => { },
};

MGridForm.propTypes = {
  width: PropTypes.number, //width in pixels of the form, defaults to 1000
  /* Array of arrays, top level array is each row, next is each control on that row
  [{
    dataType, //dataType of the field for display
    label,  //label to display on form
    value, //currently only used for DATA_TYPE_SPLIT_RADIO, fields that are DATA_TYPE_SPLIT_RADIO and share a source will count as one field, if the value will be the value of the source if its checked, value must be a string, number, or date (or i guess anything else that can be cast to a string) because it is used in the ref generation process to keep unique references to multiple split radio controls
    labelWidth, //not required - will provide a width on the label in case you need some spacing before the input fields (percentage of total field width, not form width)
    source, //key on the object provided in data prop to pull the data from, currently having two fields with the same source will cause issues, mainly with the refs created for each field because they use the source for their name
    enabled, //gets called with the changed data dataset, the source, and the current value of that field
    hasValidationError, //gets called on blur of text fields and onChange of checkbox fields with the changed data dataset, the source, and the current value of that field. return true for an error and the standard field required error message. otherwise return a string that will be the mouseover text for the error
    dataTypeOptions: {
      defaultLines, //how many lines to default the multi line text box to for DATA_TYPE_MULTI_LINE_STRING
      options, //array of { label, value, width } for DATA_TYPE_RADIO or {label, value} for DATA_TYPE_DROPDOWN. Width is not needed but will provide a percentage width in case you would like to align checkboxes
      noneOption, //if data type is DATA_TYPE_CHECKBOX you can use this to specify the none option. if the none option is checked this checkbox will get unchecked. if this checkbox is checked, the none option will get unchecked, combining noneOption with checked/unchecked will probably not currently work
      minDate, maxDate, //instanceOf(Date), if DATA_TYPE_DATE min and max dates will set the boundaries for the day selection from datepicker
      checkedValue, uncheckedValue, //if dataType is DATA_TYPE_CHECKBOX these will override the default true/false values provided from a checkbox, using these with the nonOption will probably not work
    },
    width, //percentage width (0-100) that this cell should take up. if not specified it will split the remaining space with other cells with unspecified widths
    style: {
      className //class name for the cell, accepts a string or an array of class names. the array can even be an array of arrays (in case you combine some pre existing constants that are already arrays) because the array gets flattened
      borderTop, //size in pixels of top border - default 0
      borderBottom, //size in pixels of bottom border - default 1 - last row gets defaulted to 0 so we dont have an extra thick bottom border
      borderLeft, //size in pixels of left border - default 0
      borderRight, //size in pixels of right border - default 0
    },    
    placeholder?: string; //if dataType is DATA_TYPE_DROPDOWN2 this will add the default/blank choice at the top
  }]
  ^^^^^^^^^ IF YOU ADD OR CHANGE ANYTHING HERE, ALSO BE SURE TO ADD IT TO MGridFormProps.ts IN THIS FOLDER
  */
  formDefinition: PropTypes.array,
  data: PropTypes.object, //data to fill the form with
  onChange: PropTypes.func, //method that gets called when data is changed with the following args: an object that represents the data passed in only with changed values, the source of the last field changed, the dataType of the last field changed, the value of the last field changed value, an array of all of the sources changed so far
  /* this callback function will be called with an object that has multiple functions on it that let you control MGridForm
  {
    isFormValid, //run validation on every field and return true if no validations, false otherwise (this will cause all of the validation errors to be highlighted in red)
    getModifiedData, //returns {id, value} for all of the modified fields in this form (excluding currently hidden fields that may have been modified)
  } */
  getControlMethods: PropTypes.func,
  webform: PropTypes.bool, //Prop to Enable Non-PDF styling
  portalTarget: PropTypes.func, // Function to pass down to MSelect that will return the target for the portal
  onScrollToRequiredFieldsChange: PropTypes.func, // Callback with 1 argument: getScrollToRequiredFields object
  elementIdInFocus: PropTypes.string, // Which element is in focus is determinded by MGridForm and ScrollToRequired (some react-md controls do nto have a focus() function so when scrollToRequired is used to go to next/prev element which cannot be focused, this provides a way to keep track of that). Lifted state up to Appraisal1004p
  setElementIdInFocus: PropTypes.func, // Please read elementIdInFocus explanation. As MgridForm also controls which element is in focus (the user can randomly focus any field and scrollToRequired will need a way to know it) this function can be used to save the current field in focus in state. State is lifted up to Appraisal1004p
  validationGroups: PropTypes.array, // When any of the source's value is modified and if that source is present in the validationGroups double array then all the sources part of the array will be validated together
  onBlur: PropTypes.func, // Handle onBlur event for field losing focus for subsequent handling
};

export const DISPLAY_STYLE_BOLD = "mgridform-display-style-bold";
export const DISPLAY_STYLE_LEFT = "mgridform-display-style-left";
export const DISPLAY_STYLE_CENTER = "mgridform-display-style-center";
export const DISPLAY_STYLE_RIGHT = "mgridform-display-style-right";
export const DISPLAY_STYLE_RIGHT_ALIGN_STRING = "mgridform-display-style-right-align-string";
export const DISPLAY_STYLE_MULTI_CHECKBOX_NEW_LINES = "mgridform-display-style-cb-new-lines";
export const DISPLAY_STYLE_HEADER = ["mgridform-display-style-header", DISPLAY_STYLE_CENTER];
export const DISPLAY_STYLE_SHADED = ["mgridform-display-style-shaded", DISPLAY_STYLE_BOLD, DISPLAY_STYLE_CENTER];
export const DISPLAY_STYLE_PREVENT_DISABLE_STYLE = "mgridform-display-style-prevent-disable-style";
export const DISPLAY_STYLE_SECTION_TOP = "mgridform-display-style-section-header";
export const DISPLAY_STYLE_SECTION_HEADER = [DISPLAY_STYLE_SECTION_TOP, DISPLAY_STYLE_HEADER];
export const DISPLAY_STYLE_SECTION_HEADER_NEXT_SIBLING = "mgridform-display-style-section-header-next-sibling";
export const DISPLAY_STYLE_SECTION_FOOTER = "mgridform-display-style-section-footer";
export const DISPLAY_STYLE_SECTION_FOOTER_LEFT = "mgridform-display-style-section-footer-left";
export const DISPLAY_STYLE_SECTION_FOOTER_CENTER = "mgridform-display-style-section-footer-center";
export const DISPLAY_STYLE_SECTION_FOOTER_RIGHT = "mgridform-display-style-section-footer-right";
export const DISPLAY_STYLE_SECTION_FOOTER_ONE_ELEMENT = "mgridform-display-style-section-footer-one-element";

MGridForm.parseSections = parseSections;
MGridForm.useYup = useYup;

export default MGridForm;
